import { observeNewImagesForLazyLoading } from 'client/javascript/lazy_load';
import { KEYS as k } from 'server/util/inventory_config';
import { processFilters } from 'client/javascript/pages/search/building_filtering';
import { buildBuildingCard } from 'client/shared/building_card';
import { FULL_FLOOR_OFFICE_LOW_END } from 'shared/util/search_constants';
import { trackInteraction } from 'client/self_serve/services/analytics';
import { settings as raySettings } from '@wework/ray-core';
import WeGlide from 'client/javascript/we_glide/we_glide';
import { varsFromServer } from 'client/javascript/client_side_injection_vars';
import { getStartDateFilter, formatStartDateForIQS } from 'shared/util/date';

const SELECTORS = {
  list: '.nearby-buildings__list',
  cardLink: 'a.search-card',
  container: '#nearby-buildings',
  slider: '#nearby-buildings-slider',
  slides: '#nearby-buildings-slides'
};
const NEARBY_BUILDINGS_COUNT = 2;
const DEFAULT_CAPACITY = '1';

export default class NearbyBuildings {
  static create(options) {
    return new NearbyBuildings(options);
  }

  constructor(options = {}) {
    const defaultDate = formatStartDateForIQS(getStartDateFilter());

    this.buildingsList = [];
    this.trackEventLabel = options.trackEventLabel;
    this.setupNearbyBuildings({
      capacity: DEFAULT_CAPACITY,
      startDate: defaultDate
    });
  }

  needsToFilter = capacity => capacity && +capacity < +FULL_FLOOR_OFFICE_LOW_END;

  createCards = (buildings, filtersValues, needsToFilter) => {
    return buildings.map(building => buildBuildingCard({ building, filtersValues, needsToFilter }));
  };

  calculateDistance = (lat1, lat2, lon1, lon2) => {
    const radius = 6378137;
    const deltaLat = lat2 - lat1;
    const deltaLon = lon2 - lon1;
    const sqRtFn = Math.sqrt(
      Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2
    );
    const angle = 2 * Math.asin(sqRtFn);
    return radius * angle;
  };

  sortByLocation = (originBuilding, buildingsToSort) => {
    const { lat: originBuildingLat, long: originBuildingLong } = originBuilding;
    return buildingsToSort.sort((a, b) => {
      const diffA = this.calculateDistance(originBuildingLat, a[k.lat], originBuildingLong, a[k.lng]);
      const diffB = this.calculateDistance(originBuildingLat, b[k.lat], originBuildingLong, b[k.lng]);
      return diffA - diffB;
    });
  };

  trackNearbyBuildingInteraction = event => {
    const toPath = event.currentTarget.href;
    trackInteraction(this.trackEventLabel, { toPath });
  };

  setupGallery = () => {
    if (this.glide) this.glide.destroy();
    this.glide = new WeGlide(SELECTORS.slider, {
      bound: true,
      dragThreshold: false,
      swipeThreshold: false,
      breakpoints: {
        [raySettings.breakpointsInPixels.tablet]: {
          perView: 1,
          peek: { before: 0, after: 0 },
          gap: 8,
          dragThreshold: 120,
          swipeThreshold: 80
        }
      }
    });
    this.glide.mount();
    this.glide.disableOnTablet();
  };

  setupAnalytics = () => {
    const nearbyBuildingsAnchorTags = document.querySelectorAll(SELECTORS.cardLink);
    Array.from(nearbyBuildingsAnchorTags).forEach(anchorTag =>
      anchorTag.addEventListener('click', this.trackNearbyBuildingInteraction)
    );
  };

  processFiltersAndSortBuildings = (building, marketBuildings, selectionInput) => {
    const filteredMarketBuildings = marketBuildings.filter(b => {
      return b[k.name] !== building.name;
    });
    const [availableMarketBuildings, unavailableBuildings] = processFilters(selectionInput, filteredMarketBuildings);

    // Sorting available buildings
    const availableBuildingsSortedByLocation = this.sortByLocation(building, availableMarketBuildings);
    const sortedNearbyBuildings = availableBuildingsSortedByLocation.filter(
      b => !b.imperfectCapacity && !b.imperfectDate
    );
    const imperfectSortedNearbyBuildings = availableBuildingsSortedByLocation.filter(
      b => b.imperfectCapacity || b.imperfectDate
    );

    const firstTwoBuildings = sortedNearbyBuildings.slice(0, 2);
    if (firstTwoBuildings.length < NEARBY_BUILDINGS_COUNT) {
      // Sorting unavailable buildings if there are not enough buildings
      const unavailableBuildingsSortedByLocation = this.sortByLocation(building, unavailableBuildings);

      const backfilledBuildingList = imperfectSortedNearbyBuildings
        .concat(unavailableBuildingsSortedByLocation)
        .slice(0, NEARBY_BUILDINGS_COUNT - firstTwoBuildings.length);

      this.buildingsList = firstTwoBuildings.concat(backfilledBuildingList);
    } else {
      this.buildingsList = firstTwoBuildings;
    }
  };

  renderCards = async filterValues => {
    const { capacity } = filterValues;
    const date = filterValues.date || filterValues.startDate;
    const { building, marketBuildings } = varsFromServer;
    const needsToFilter = this.needsToFilter(capacity);
    if (needsToFilter) {
      await this.processFiltersAndSortBuildings(building, marketBuildings, { date, capacity });
    }
    if (this.buildingsList.length) {
      const finalCards = this.createCards(this.buildingsList, { date, capacity }, needsToFilter);
      const nearbyBuildings = document.querySelector(SELECTORS.slides);
      nearbyBuildings.innerHTML = '';
      finalCards.forEach(card => nearbyBuildings.appendChild(card));
      observeNewImagesForLazyLoading();
    }
  };

  setupNearbyBuildings = async filterValues => {
    await this.renderCards(filterValues);
    if (this.buildingsList.length) {
      this.setupGallery();
      this.setupAnalytics();
    } else {
      document.querySelector(SELECTORS.container).innerHTML = '';
    }
  };
}
